import { Component, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { SHARED_IMPORTS } from '../shared/shared-imports';
import { WINDOW } from '../shared/tokens/window.token';
import { ContentService } from '../../services/content.service';
import { OrderByPipe } from '../../pipes/order-by.pipe';


// Angular implementation using state management

@Component({
	standalone: true,
	selector: 'location-component',
	templateUrl: 'components/location/location.component.html',
	// styleUrls: ['components/location/location.component.css'],
	imports: [...SHARED_IMPORTS, 
		OrderByPipe, 
		MatSnackBarModule,
		MatButtonModule,
		MatMenuModule,
		MatIconModule,
		MatTooltipModule,
		MatCheckboxModule],
	schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class LocationComponent implements OnInit, OnDestroy {

	// Angular services
	private Content = inject(ContentService);
	private http = inject(HttpClient);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private windowRef = inject(WINDOW);
	private snack = inject(MatSnackBar);


	public getContent: any;
	public withNavbar: boolean = true;
	public showTemplate: boolean = false;
	public templateApplied: boolean = false;
	public readMode: boolean = false;
	public selectedTemplateId: string = '';
	public selectedTemplateName: string = '';
	public selectedUnitType: string = '';
	public checkedSameUnitType: number = 0;
	public tempNode: any = {};
	public filteredListOfLocations: any[] = [];
	public filter: any = {};
	public filterMobile: boolean = false;
	public filterActivated: boolean = false;
	public locationClass: string = '';
	public filterUnchecked: boolean = false;
	public showLocations: boolean = false;
	public manageTemplatesSelected: boolean = false;
	public siteLevelActive: boolean = false;
	public positionIdToChangeTemplate: string = '';
	public preselectedTemplates: string[] = [];
	public templateClicked: boolean = false;
	public selectedPosition: any = null;
	public isUnitTypeSelected: boolean = false;
	public isSynched: boolean = true;
	public isUnitAttached: boolean = false;
	public isUnitTypeSelectedFromTemplateDetail: boolean = false;
	public unitTypes: Record<string, boolean> = {
		'IVC': false,
		'SC+Scantainer': false,
		'IsoCage': false
	};
	public unitType: string = 'IVC';
	public currentNode: any = null;
	public overlay: string | false = false;
	public overlayTemplate: boolean = false;
	public templatePreselected: boolean = false;
	
	// Angular collapse state management (replacing Bootstrap collapse)
	public filterCollapsed: boolean = true;
	public selectedFilterLocations: Set<string> = new Set();
	public templates: any[] | null = null;
	public currentUser: any = null;
	public goingBack: boolean = false;
	public isLoaded: boolean = false;
	public showLimits: boolean = null as unknown as boolean;
	public locationStructure: any = null;
	private bodyClickHandler: any;
	public overlayOpen = false;
	public measurementsTableColumns = ['Temperature', 'CageHumidity', 'AirChange', 'AirFlowDiff', 'PressurePa'];

	public conditionNames = [
		{ tempPropTarget: "TemperatureTarget", tempPropAllowedDev: "TemperatureAllowedDeviation", name: "Temperature", unit: "°C", unitType: "IVC IsoCage SC+Scantainer" },
		{ tempPropTarget: "CageHumidityTarget", tempPropAllowedDev: "CageHumidityAllowedDeviation", name: "Humidity", unit: "%", unitType: "IVC IsoCage SC+Scantainer" },
		{ tempPropTarget: "AirChangeTarget", tempPropAllowedDev: "AirChangeAllowedDeviation", name: "AirChange", unit: "x", unitType: "IVC SC+Scantainer" },
		{ tempPropTarget: "AirFlowDiffTarget", tempPropAllowedDev: "AirFlowDiffAllowedDeviation", name: "AirFlowDiff", unit: "%", unitType: "IVC" },
		{ tempPropTarget: "IsPressurePositive", name: "PressureDiff", unit: "", unitType: "IVC" },
		{ tempPropTarget: "ExhaustAirFlowAllowedMinimum", name: "ExhaustAirFlow", unit: "m3/h", unitType: "IsoCage" },
		{ tempPropTarget: "UpsPctBatteryAllowedMinimum", name: "UpsBattery", unit: "%", unitType: "IsoCage" },
		{ tempPropTarget: "PressurePaTarget", tempPropAllowedDev: "PressurePaAllowedDeviation", name: "Pressure", unit: "Pa", unitType: "IsoCage SC+Scantainer" },
		{ name: "UsedOn", unit: "0 positions", unitType: "IsoCage IVC SC+Scantainer" }
	];


	// Browser storage replacements for AngularJS services

	constructor() { }

	private store = {
		set: (key: string, value: any) => localStorage.setItem(key, JSON.stringify(value)),
		get: (key: string) => {
			const item = localStorage.getItem(key);
			return item ? JSON.parse(item) : null;
		},
		remove: (key: string) => localStorage.removeItem(key)
	};
	private $localStorage = {
		get showLimits() { return JSON.parse(localStorage.getItem('showLimits') || 'true'); },
		set showLimits(value: boolean) { localStorage.setItem('showLimits', JSON.stringify(value)); },
		get currentNode() { return JSON.parse(localStorage.getItem('currentNode') || 'null'); },
		set currentNode(value: any) { localStorage.setItem('currentNode', JSON.stringify(value)); },
		get filterUnchecked() { return JSON.parse(localStorage.getItem('filterUnchecked') || 'false'); },
		set filterUnchecked(value: boolean) { localStorage.setItem('filterUnchecked', JSON.stringify(value)); },
		get listLocations() { return JSON.parse(localStorage.getItem('listLocations') || '[]'); },
		set listLocations(value: any) { localStorage.setItem('listLocations', JSON.stringify(value)); }
	};
	// ---------------- Lifecycle ----------------
	ngOnInit(): void {
		this.getContent = this.Content.getContent.bind(this.Content);
		this.withNavbar = true;

		// Note: Removed transition hooks since we're using Angular Router
		// The $rootScope.actualLocation functionality can be replaced with a service if needed

		this.showLimits = this.$localStorage.showLimits ?? true;
		this.currentNode = this.$localStorage.currentNode ?? null;

		this.getFinalLocationOverview();
		this.getCurrentUser();
		this.registerBodyClickHandler();

		this.windowRef.addEventListener('popstate', this.handlePopState);
	}



	ngOnDestroy(): void {
		if (this.bodyClickHandler) {
			document.body.removeEventListener('click', this.bodyClickHandler);
		}
		this.windowRef.removeEventListener('popstate', this.handlePopState);
	}

	// ---------------- History / Nav ----------------
	private handlePopState = (): void => {
		// TODO: Implement proper navigation history tracking if needed
		// For now, always refresh the location overview
		this.getLocationOverview(this.siteLevelActive);
	};

	registerBodyClickHandler(): void {
		this.bodyClickHandler = (evt: MouseEvent) => {
			const target = evt.target as HTMLElement;
			const isInsideDropdown = target.closest('#viewDropdown');
			const isContentView = target.id === 'contentView';
			const isViewBtn = target.id === 'viewBtn';

			if (isInsideDropdown || isContentView || isViewBtn) return;

			const viewDropdown = document.getElementById('viewDropdown');
			if (viewDropdown) {
				viewDropdown.classList.add('no-display');
			}
		};

		document.body.addEventListener('click', this.bodyClickHandler);
	}

	// ---------------- Data ----------------
	getLocationOverview(withSites: boolean) {
		this.filterActivated = false;
		this.showLocations = false;
		const url = withSites ? '/api/Location/LocationOverviewWithSites' : '/api/Location/LocationOverview';

		this.http.get<any>(url).subscribe((response) => {
			this.filterUnchecked = this.$localStorage.filterUnchecked;
			this.$localStorage.filterUnchecked = false;
			this.locationStructure = response;

			let node: any = null;

			const routePath = this.route.snapshot.params['path'];
			if (routePath) {
				node = this.getNodeByPath(response.Roots, routePath, false);

				if (routePath !== '') {
					if (node.NodeTypeStr === 'Site') {
						this.currentNode = { Children: [node], Name: 'Locations' };
					} else {
						this.filterActivated = true;
						this.currentNode = { Children: [node], Name: 'Rooms' };
					}

					this.showLocations = withSites;

					if (this.filter.locationsList) {
						this.$localStorage.currentNode = this.currentNode;
					}

					if (withSites) {
						const tempNode = this.getNodeByPath(this.locationStructure.Roots, routePath, true);
						this.tempNode = { Children: [tempNode], Name: 'Sites' }.Children[0];
					} else {
						this.tempNode = { Children: response.Roots, Name: 'Rooms' };
					}
				}
			} else {
				const locationName = withSites ? 'Site' : 'Locations';
				this.currentNode = node || { Children: response.Roots, Name: locationName };
				this.tempNode = withSites ? this.currentNode : { Children: response.Roots, Name: locationName };
				this.$localStorage.currentNode = this.currentNode;
			}

		// TODO: Implement proper navigation history tracking if needed
		// For now, always clear location filters when coming from other routes
		this.filter.locationsList = [];
		this.filteredListOfLocations = [];
		
		const tempNode = { Children: response.Roots, Name: withSites ? 'Site' : 'Locations' };

		const list = this.$localStorage.listLocations || [];
		for (const filteredKey of list) {
			for (const tempFilterNode of tempNode.Children) {
				if (withSites) {
					for (const child of tempFilterNode.Children) {
						if (filteredKey === child.Id) {
							this.filteredListOfLocations.push(child);
						}
					}
				} else {
					if (filteredKey === tempFilterNode.Id) {
						this.filteredListOfLocations.push(tempFilterNode);
					}
				}
			}
		}

		if (this.filteredListOfLocations.length > 0) {
			this.currentNode = { Children: this.filteredListOfLocations, Name: 'Rooms' };
			this.$localStorage.filterUnchecked = false;
		} else {
			this.$localStorage.filterUnchecked = true;
		}

		this.$localStorage.currentNode = this.currentNode;
		
		if (node !== null) {
			this.$localStorage.listLocations = node.Id;
		}

		this.isLoaded = true;
		this.uncheckAllPositions();
		});
	}

	uncheckAllPositions(): void {
		this.selectedUnitType = '';
		const sites = this.currentNode?.Children || [];

		for (const site of sites) {
			const locations = site.Children || [];
			for (const location of locations) {
				const rooms = location.Children || [];
				for (const room of rooms) {
					room.applyTemplate = false;

					if (room.LocationOverviewPosition !== null) {
						room.LocationOverviewPosition.applyTemplate = false;
					}

					const positions = room.Children || [];
					for (const position of positions) {
						if (position.LocationOverviewPosition) {
							position.LocationOverviewPosition.applyTemplate = false;
						}
					}
				}
			}
		}
		this.removeTemplatesFromPreselectedList();
	}

	selectUnitType(id: string): void {
		const keys = Object.keys(this.unitTypes);
		this.unitType = keys[+id];
		this.selectedUnitType = this.unitType;
		this.getTemplates();
	}

	openMenu($mdMenu: any, $event: MouseEvent): void {
		$mdMenu.open($event);
		const menu = document.querySelector('.md-open-menu-container');
		if (menu) {
			menu.classList.add('locationsMenu');
		}
	}

	getTemplates(): void {
		let unitType = this.unitType;
		if (unitType === 'SC+Scantainer') {
			unitType = 'SC%2BScantainer';
		}
		this.http.get<any[]>('/api/UnitConditionTemplate/GetTemplates?unitType=' + unitType).subscribe((data) => {
			const response = (data as any);
			if (response !== '' && response !== null && response !== 'undefined') {
				this.templates = response;
			} else {
				this.templates = [];
			}
			this.templates?.sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));
			this.preselectTemplate();
		});
	}

	preselectTemplate(): void {
		if (!this.templates) return;
		for (const t of this.templates) {
			t.templateUsed = false;
			if (this.preselectedTemplates.length > 0) {
				for (const pre of this.preselectedTemplates) {
					if (t.Id === pre) t.templateUsed = true;
				}
			}
		}
	}

	removeTemplatesFromPreselectedList(): void {
		this.preselectedTemplates.splice(0, this.preselectedTemplates.length);
	}

	getBackButtonTitle(): string {
		let title = this.Content.getContent('LocationOverview.Locations');
		if (this.siteLevelActive && this.currentNode?.Name === 'Rooms') {
			title = this.currentNode.Children[0].LocationPath.substr(1);
		}
		return title;
	}

	toggleOverlay(panel: string): void {
		this.fetchFilter();
		if (!this.overlay) {
			this.overlay = panel;
		} else {
			this.overlay = this.overlay === panel ? false : panel;
		}
		
		// Handle filter collapse with Angular state management
		if (panel === 'filter') {
			this.filterCollapsed = !this.filterCollapsed;
		}
	}

	public overlayClick(): void {
					// Use Angular state management for collapse functionality
		this.filterCollapsed = true;
		
		if (this.overlay === 'filter') {
			this.applyFilter();
		}
		this.overlay = false;
		this.templateClicked = false;
	}

	applyFilter(): void {
		// Use Angular state management
		this.filter.locationsList = Array.from(this.selectedFilterLocations);

		this.$localStorage.listLocations = this.filter.locationsList;
		this.filterMobile = false;
		this.store.set('filterLocations', this.filter);
		this.filterActivated = true;
		this.doFilter();
		this.overlay = false;

		// Use Angular state management
		this.filterCollapsed = true;
	}

	doFilter(): void {
		this.filteredListOfLocations = [];

		const wanted: string[] = this.filter.locationsList || [];
		const children = (this.tempNode?.Children || []) as any[];

		for (const filteredKey of wanted) {
			for (const child of children) {
				if (filteredKey === child.Id) {
					this.filteredListOfLocations.push(child);
				}
			}
		}

		if (this.filteredListOfLocations.length > 0) {
			this.currentNode = { Children: this.filteredListOfLocations, Name: 'Rooms' };
			this.$localStorage.filterUnchecked = false;
		} else {
			this.$localStorage.filterUnchecked = true;
		}

		this.$localStorage.currentNode = this.currentNode;
	}

	getTableClass(node: any): string {
		if (!node) return '';
		let tableClass = `level${node.Level}`;

		if (node.NodeTypeStr !== 'Site') {
			tableClass += ' bordLocations';
		} else {
			if (!this.showLocations) {
				tableClass += ' bordSites';
			}
		}

		if (this.currentNode.Name === 'Locations') {
			tableClass += ' locationLevel';
		} else if (this.currentNode.Name === 'Rooms') {
			tableClass += ' roomLevel';
		}

		return tableClass;
	}

	getLocationTitleClass(): string {
		if (this.showLocations && !this.filterActivated) return 'locationTitleFilteredRoot';
		else if (this.filterActivated) return 'locationTitleFiltered';
		else return 'locationTitle';
	}

	getLocationCircleClass(): string {
		return this.filterActivated ? 'locationCircleFiltered' : 'locationCircle';
	}

	getLocationNameClass(node: any): string {
		if (node && node.NodeTypeStr !== 'Site') {
			return this.filterActivated ? 'locationNameFiltered' : 'locationName';
		}
		return '';
	}

	getNumberOfRooms(node: any): number {
		let counter = 0;
		for (let i = 0; i < node.Children.length; i++) {
			for (let j = 0; j < node.Children[i].Children.length; j++) {
				counter++;
			}
		}
		return counter;
	}

	getTemplatePressure(isPressurePositive: boolean | null | undefined): string {
		if (isPressurePositive === true) return '+';
		else if (isPressurePositive === false) return '-';
		else return '';
	}

	backClickToLocation(): void {
		this.manageTemplatesSelected = false;
		this.checkedSameUnitType = 0;
	}

	getMeasurementValue(position: any, measurement: string): string | undefined {
		if (measurement === 'PressurePa') {
			const pos = position.LocationOverviewPosition;
			if (pos.PressurePa != null && pos.UnitTypeName !== 'IVC') {
				return Math.round(pos.PressurePa) + this.getMeasurementSign(measurement);
			} else if (pos.PressureTarget != null) {
				return pos.PressureTarget > 0 ? '+' : '-';
			}
			return undefined;
		}
		const value = position.LocationOverviewPosition[measurement];
		if (measurement === 'Temperature') {
			return `${(Math.round(value * 10) / 10).toFixed(1)}${this.getMeasurementSign(measurement)}`;
		}
		return `${Math.round(value)}${this.getMeasurementSign(measurement)}`;
	}

	getLimitValue(position: any, measurement: string, highOrLow: string | 'HighLimit'): string {
		const value = position.LocationOverviewPosition[`${measurement}${highOrLow}`];
		const noValue = measurement === 'PressurePa' ? '' : '-';
		return value != null ? `${value}${this.getMeasurementSign(measurement)}` : noValue;
	}

	getMeasurementSign(measurement: string) {
		let sign = '%';
		if (measurement === 'PressurePa') sign = 'Pa';
		else if (measurement === 'AirChange') sign = 'x';
		else if (measurement === 'Temperature') sign = '°C';
		return sign;
	}

	alarmClick(node: any): void {
		this.router.navigate(['/AlarmOverview'], { queryParams: { Location: node.Name } });
	}

	outsideLimits(current: number, min: number | null, max: number | null): boolean {
		if (min !== null && max !== null) {
			return current < min || current > max;
		}
		return false;
	}

	getMeasurementClass(current: number, min: number | null, max: number | null): string {
		return this.outsideLimits(current, min, max) ? 'outsideLimitsText addRightTopPadding' : '';
	}

	getMeasurementValueAndClass(position: any, measurement: string) {
		const pos = position.LocationOverviewPosition;
		return this.getMeasurementClass(pos[measurement], pos[`${measurement}LowLimit`], pos[`${measurement}HighLimit`]);
	}

	printDashes(position: any): boolean {
		return (
			position.LocationOverviewPosition.AirFlowDiff == null ||
			position.Decoration.UnitsOnlineCount === 0
		);
	}

	showAirFlowDiff(position: any): boolean {
		const type = position.LocationOverviewPosition.UnitTypeName;
		return type !== 'IsoCage' && type !== 'SC+Scantainer';
	}

	showColumValue(position: any, measurement: string) {
		const value = position.LocationOverviewPosition[measurement];
		let condition = value != null;
		if (measurement === 'AirFlowDiff') {
			return value != null && position.Decoration.UnitsOnlineCount == 1 && this.showAirFlowDiff(position);
		} else if (measurement === 'PressurePa') {
			condition = (value != null || position.LocationOverviewPosition.PressureTarget != null);
		}
		return condition && position.Decoration.UnitsOnlineCount == 1;
	}

	showDashes(position: any, measurement: string) {
		if (measurement === 'AirFlowDiff') {
			if (this.printDashes(position) && this.showAirFlowDiff(position)) {
				return '-  -';
			}
		} else {
			if (!this.showColumValue(position, measurement)) {
				if (measurement === 'PressurePa') {
					return '';
				} else {
					return '-  -';
				}
			}
		}
		return '';
	}

	highLimits(position: any, measurement: string): boolean {
		const pos = position.LocationOverviewPosition;
		return pos[`${measurement}HighLimit`] !== null ? pos[measurement] > pos[`${measurement}HighLimit`] : false;
	}

	lowLimits(position: any, measurement: string): boolean {
		const pos = position.LocationOverviewPosition;
		return pos[`${measurement}LowLimit`] !== null ? pos[measurement] < pos[`${measurement}LowLimit`] : false;
	}

	nodeClickCheck(node: any): boolean {
		if (this.currentNode.Name === 'Locations') {
			return node.NodeTypeStr !== 'Site';
		}
		return true;
	}

	nodeClick(node: any): void {
		this.filterMobile = false;
		this.isLoaded = false;
		this.locationClass = 'locationName';
		this.goingBack = false;

		const appendToPath = (cur: string | undefined, segment: string) => {
			const base = (cur || '').replace(/\/+$/, '');      // strip trailing /
			const tail = (segment || '').replace(/^\/+/, '');  // strip leading /
			return base ? `${base}/${tail}` : tail;
		};

		if (node.NodeTypeStr === 'Position') {
			if (node.AnchoredUnitId) {
				this.router.navigate(['/UnitDetail', node.AnchoredUnitId]);
			} else {
				this.filterActivated = true;
			}
		} else if (!this.filterActivated && this.nodeClickCheck(node)) {
			const curPath = this.route.snapshot.params['path'] || '';
			const nextPath = appendToPath(curPath, node.Name);

			if (this.siteLevelActive) {
				if (node.NodeTypeStr === 'Site') {
					this.showLocations = true;
					// Navigate to location with path parameter
					this.router.navigate(['/LocationOverview', nextPath], { replaceUrl: true });
					this.currentNode = { Children: [node], Name: 'Locations' };
				} else {
					this.filterActivated = true;
					this.showLocations = true;
					// Navigate to location with path parameter
					this.router.navigate(['/LocationOverview', nextPath], { replaceUrl: true });
					this.currentNode = { Children: [node], Name: 'Rooms' };
					this.$localStorage.listLocations = node.Id;

					const tempNode = this.getNodeByPath(this.locationStructure.Roots, curPath, true);
					this.tempNode = { Children: [tempNode], Name: 'Locations' }.Children[0];
				}
			} else {
				this.filterActivated = true;
				// Navigate to location with path parameter
				this.router.navigate(['/LocationOverview', nextPath], { replaceUrl: true });
				this.currentNode = { Children: [node], Name: 'Rooms' };
				this.$localStorage.listLocations = node.Id;
			}

			this.$localStorage.currentNode = this.currentNode;
		}

		this.isLoaded = true;
	}



	getPositionWarningSymbolStyle(position: any): string | undefined {
		if (position.Decoration.AlarmsCriticalCount > 0) return 'criticalAlarmSymbol';
		if (position.Decoration.AlarmsNoncriticalCount > 0) return 'noncriticalAlarmSymbol';
		if (position.Decoration.AlarmsDismissableCount > 0) return 'dismissableAlarmSymbol';
		return undefined;
	}

	getCages(value: { Key: string, Value: string }[]): string {
		return value.map(cage => `${cage.Value} x ${cage.Key}`).join('\n');
	}

	getPositionWarningSymbol(position: any): string | undefined {
		if (
			position.Decoration.AlarmsCriticalCount > 0 ||
			position.Decoration.AlarmsNoncriticalCount > 0 ||
			position.Decoration.AlarmsDismissableCount > 0
		) {
			return 'warning';
		}
		return undefined;
	}

	fetchFilter(): void {
		// Use Angular state management
		this.selectedFilterLocations.clear();

		if (this.currentNode.Name === 'Rooms' || this.currentNode.Name === 'Locations') {
			for (const child of (this.currentNode.Children || [])) {
				this.selectedFilterLocations.add(child.Id);
			}
		} else {
			this.selectedFilterLocations.add(this.currentNode.Id);
		}
	}

	toBeFiltered(node: any): void {
	// Simple toggle logic for filter selection
		if (this.selectedFilterLocations.has(node.Id)) {
			this.selectedFilterLocations.delete(node.Id);
		} else {
			this.selectedFilterLocations.add(node.Id);
		}
	}


	backClick(): void {
		this.filterMobile = false;
		this.locationClass = '';
		this.filterActivated = false;
		this.overlay = false;
		// Use Angular state management
		this.filterCollapsed = true;
		this.$localStorage.filterUnchecked = false;
		this.goingBack = true;
		this.store.remove('filterLocations');
		this.filter.locationsList = [];
		this.uncheckAllPositions();

		const currentPath = this.route.snapshot.params['path'];
		if (currentPath) {
			const path = currentPath.substr(0, currentPath.lastIndexOf('/'));

			if (this.siteLevelActive) {
				const node = this.getNodeByPath(this.locationStructure.Roots, currentPath, true);

				if (this.currentNode.Name === 'Rooms') {
					this.currentNode = { Children: [node], Name: 'Locations' };
					this.filterActivated = false;
				} else {
					this.currentNode = { Children: this.locationStructure.Roots, Name: 'Site' };
					this.showLocations = false;
				}
			} else {
				const prevNode = this.getNodeByPath(this.locationStructure.Roots, path, false);
				this.currentNode = prevNode || { Children: this.locationStructure.Roots, Name: 'Locations' };
			}

			this.router.navigate(['/LocationOverview', path], { replaceUrl: true });
		} else {
			this.router.navigate(['/Main']);
		}
	}


	differentUnitTypesInTheRoom(childNode: any): boolean {
		if (childNode.Children !== null) {
			for (let i = 0; i < childNode.Children.length - 1; i++) {
				const curr = childNode.Children[i].LocationOverviewPosition.UnitTypeId;
				const next = childNode.Children[i + 1].LocationOverviewPosition.UnitTypeId;
				if (curr !== null && next !== null && curr !== next) {
					return true;
				}
			}
		}
		return false;
	}

	differentUnitType(position: any): boolean {
		if (position.LocationOverviewPosition != null) {
			const unitTypeName = position.LocationOverviewPosition.UnitTypeName;
			if (
				!this.manageTemplatesSelected ||
				this.selectedUnitType === '' ||
				unitTypeName === '' ||
				unitTypeName == null ||
				this.selectedUnitType === unitTypeName
			) {
				return false;
			}
		}
		return true;
	}

	checkPosition(node: any, position: any): void {
		const checked = position.LocationOverviewPosition.applyTemplate;
		const unitType = position.LocationOverviewPosition.UnitTypeName;
		const templateId = position.LocationOverviewPosition.UnitConditionTemplateId;

		if (checked === true) {
			if (templateId !== null && this.preselectedTemplates.indexOf(templateId) === -1) {
				this.preselectedTemplates.push(templateId);
				this.templatePreselected = true;
			}

			if (this.selectedUnitType === "") {
				if (unitType !== null) {
					this.selectedUnitType = unitType;
					this.checkedSameUnitType++;
				}
			} else {
				if (unitType !== null && unitType === this.selectedUnitType) {
					this.checkedSameUnitType++;
				}
			}
		} else {
			for (let i = this.preselectedTemplates.length - 1; i >= 0; i--) {
				if (this.preselectedTemplates[i] === templateId) {
					this.preselectedTemplates.splice(i, 1);
				}
			}

			if (this.preselectedTemplates.length === 0) {
				this.templatePreselected = false;
			}

			if (unitType !== null && unitType === this.selectedUnitType && this.checkedSameUnitType > 0) {
				this.checkedSameUnitType--;
			}

			if (this.checkedSameUnitType === 0) {
				this.selectedUnitType = "";
			}
		}

		const count = node.Children.length;
		let tempCount = 0;

		for (let i = 0; i < node.Children.length; i++) {
			if (node.Children[i].LocationOverviewPosition.applyTemplate === true) {
				tempCount++;
			}
		}

		node.applyTemplate = count === tempCount;
	}

	noPositionSelected(): boolean {
		let room: any = {};
		for (let k = 0; k < this.currentNode.Children.length; k++) {
			for (let i = 0; i < this.currentNode.Children[k].Children.length; i++) {
				room = this.currentNode.Children[k].Children[i];
				for (let j = 0; j < room.Children.length; j++) {
					if (room.Children[j].LocationOverviewPosition.applyTemplate === true) {
						return false;
					}
				}
			}
		}
		return true;
	}

	noTemplateSelected(): boolean {
		if (this.templateApplied) return true;
		if (this.templates != null) {
			for (let i = 0; i < this.templates.length; i++) {
				if (this.templates[i].templateUsed && !this.templatePreselected) {
					return false;
				}
			}
			return true;
		}
		return true;
	}

	checkAllPositions(childNode: any, node: any): void {
		const rooms = node.Children;
		let unitTypeSelectedInDifferentRoom = false;

		for (let room = 0; room < rooms.length; room++) {
			const positions = rooms[room].Children;

			for (let pos = 0; pos < positions.length; pos++) {
				const position = positions[pos].LocationOverviewPosition;

				if (rooms[room] !== childNode) {
					if (position.UnitTypeName === this.selectedUnitType && position.applyTemplate) {
						unitTypeSelectedInDifferentRoom = true;
					}
				} else {
					if (childNode.applyTemplate === true) {
						if (position.UnitTypeName !== null && position.UnitTypeName !== '') {
							if (this.selectedUnitType === '' || this.selectedUnitType === null) {
								this.selectedUnitType = position.UnitTypeName;
								this.checkedSameUnitType++;
							} else if (position.UnitTypeName === this.selectedUnitType) {
								this.checkedSameUnitType++;
							}
						}
					}
				}
			}
		}

		if (childNode.applyTemplate === true) {
			for (let i = 0; i < childNode.Children.length; i++) {
				const childPosition = childNode.Children[i].LocationOverviewPosition;

				if (
					childPosition.UnitTypeName === '' ||
					childPosition.UnitTypeName === null ||
					childPosition.UnitTypeName === this.selectedUnitType
				) {
					childPosition.applyTemplate = true;
					this.addTemplateIdToPreselectedList(childPosition.UnitConditionTemplateId);
				}
			}
		} else {
			for (let i = 0; i < childNode.Children.length; i++) {
				const childPosition = childNode.Children[i].LocationOverviewPosition;

				childPosition.applyTemplate = false;

				if (
					(childPosition.UnitTypeName !== '' && childPosition.UnitTypeName !== null) &&
					childPosition.UnitTypeName === this.selectedUnitType
				) {
					this.checkedSameUnitType--;
				}
			}

			if (!unitTypeSelectedInDifferentRoom) {
				this.selectedUnitType = '';
				this.unitType = '';
				this.checkedSameUnitType = 0;
			}
		}
	}

	addTemplateIdToPreselectedList(templateId: string): void {
		if (this.preselectedTemplates.indexOf(templateId) === -1) {
			this.preselectedTemplates.push(templateId);
			this.templatePreselected = true;
		}
	}

	getUnitTypeName(unitTypeName: string): string {
		return this.Content.getContent(`UnitType.${unitTypeName}`);
	}

	getUnitTypeBtnClass(unitTypeName: string): string {
		let className = this.unitType === unitTypeName ? 'unitTypeTemplateSelectedBtn' : 'unitTypeTemplateAvailableBtn';
		if (this.selectedUnitType && this.selectedUnitType === unitTypeName) {
			className += ' unitTypeLocked';
		}
		return className;
	}

	isUnitTypeButtonDisabled(unitTypeName: string): boolean {
		if (this.selectedUnitType !== '') {
			if (this.isUnitTypeSelected) {
				return (this.unitType === this.selectedUnitType && this.unitType !== unitTypeName && !this.isUnitTypeSelectedFromTemplateDetail);
			}
			return false;
		}
		return false;
	}

	manageTemplates(): void {
		this.templateApplied = false;
		this.isSynched = true;
		this.showTemplate = true;
		this.templateClicked = false;
		this.toggleOverlayTemplate();

		if (this.selectedUnitType === null || this.selectedUnitType === '') {
			this.selectedUnitType = 'IVC';
			this.isUnitTypeSelected = false;
		} else {
			this.isUnitTypeSelected = true;
		}

		this.unitType = this.selectedUnitType;
		this.getTemplates();
	}

	switchTemplate(template: any): void {
		const elementPos = this.templates?.indexOf(template) ?? -1;

		if (!this.templates) return;

		for (let i = 0; i < this.templates.length; i++) {
			if (this.templatePreselected) {
				this.templates[i].templateUsed = false;
			} else if (i !== elementPos) {
				this.templates[i].templateUsed = false;
			}
		}

		this.selectedTemplateId = template.Id;
		this.selectedTemplateName = template.Name;
		this.templatePreselected = false;
		this.removeTemplatesFromPreselectedList();
	}

	incorrectUnitType(position: any): boolean {
		const unitTypeName = position.LocationOverviewPosition.UnitTypeName;
		const templateUnitTypeName = position.LocationOverviewPosition.UnitTemplateUnitTypeName;
		return unitTypeName !== null && unitTypeName !== templateUnitTypeName;
	}

	getTemplateName(position: any): string {
		const wrongUnitType = this.incorrectUnitType(position);
		if (wrongUnitType) {
			return this.Content.getContent('LocationOverview.IncorrectTemplateForDevice');
		} else {
			return position.LocationOverviewPosition.UnitConditionTemplateName;
		}
	}

	applyOrRemoveTemplate(removeTemplate: boolean): void {
		document.body.style.cursor = 'progress';
		this.templateApplied = true;

		let room: any = {};
		const positionsToApply: any = {
			TemplateId: this.selectedTemplateId,
			ListOfPositions: null
		};

		const ListOfPositions: any[] = [];
		const roomsToUncheck: any[] = [];
		const positionsToChangeTemplateName: any[] = [];
		let url = '';

		if (this.positionIdToChangeTemplate !== '') {
			ListOfPositions.push({ PositionId: this.positionIdToChangeTemplate });
			if (this.selectedPosition !== null) {
				positionsToChangeTemplateName.push(this.selectedPosition.LocationOverviewPosition);
			}
		} else {
			for (let k = 0; k < this.currentNode.Children.length; k++) {
				for (let i = 0; i < this.currentNode.Children[k].Children.length; i++) {
					room = this.currentNode.Children[k].Children[i];
					for (let j = 0; j < room.Children.length; j++) {
						if (room.Children[j].LocationOverviewPosition.applyTemplate === true) {
							ListOfPositions.push({ PositionId: room.Children[j].LocationOverviewPosition.PositionId });
							positionsToChangeTemplateName.push(room.Children[j].LocationOverviewPosition);
						}
					}
					if (room.applyTemplate) {
						roomsToUncheck.push(room);
					}
				}
			}
		}

		if (!removeTemplate) {
			positionsToApply.ListOfPositions = ListOfPositions;
			url = 'api/UnitConditionTemplate/AssignTemplateToPositions';
		} else {
			url = 'api/UnitConditionTemplate/RemoveTemplateFromPositions';
		}

		const dataToSend = removeTemplate ? ListOfPositions : positionsToApply;

		this.http.post<any>(url, dataToSend, {
			headers: new HttpHeaders({ 'Content-type': 'application/json' })
		}).subscribe((response) => {
			this.close();
			this.updateTemplateName(positionsToChangeTemplateName, removeTemplate);
			this.manageTemplatesSelected = false;
			this.selectedUnitType = '';
			this.templateAssignedOrRemovedFeedback(response, removeTemplate);

			if (roomsToUncheck.length > 0) {
				this.uncheckRoom(roomsToUncheck);
			}

			this.positionIdToChangeTemplate = '';
			this.removeTemplatesFromPreselectedList();
			document.body.style.cursor = 'default';
		});
	}

	startManageTemplateMode(): void {
		this.manageTemplatesSelected = true;
		this.templateClicked = false;
	}

	uncheckRoom(room: any[]): void {
		for (let i = 0; i < room.length; i++) {
			room[i].applyTemplate = false;
		}
	}

	updateTemplateName(positionsToChangeTemplateName: any[], removeTemplate: boolean): void {
		for (let i = 0; i < positionsToChangeTemplateName.length; i++) {
			positionsToChangeTemplateName[i].UnitConditionTemplateName = removeTemplate ? null : this.selectedTemplateName;
			positionsToChangeTemplateName[i].UnitConditionTemplateId = removeTemplate ? null : this.selectedTemplateId;
			positionsToChangeTemplateName[i].UnitTemplateUnitTypeName = this.selectedUnitType;
			positionsToChangeTemplateName[i].applyTemplate = false;
		}
	}

	toggleOverlayTemplate(): void {
		this.overlayTemplate = !this.overlayTemplate;
	}

	close(): void {
		document.getElementById('templateName')?.classList.remove('notClickable');

		this.isSynched = true;
		this.isUnitAttached = false;
		this.showTemplate = false;
		this.overlayTemplate = false;
		this.templateClicked = false;
		this.positionIdToChangeTemplate = '';
		this.isUnitTypeSelectedFromTemplateDetail = false;

		if (!this.isUnitTypeSelected) {
			this.selectedUnitType = '';
		}

		this.unitType = '';
		document.body.classList.remove('noscroll');
		this.removeTemplatesFromPreselectedList();
	}

	closeTemplateDetail(): void {
		this.close();
		this.selectedUnitType = '';
	}

	toggleMenu(): void {
		const dropdown = document.getElementById('viewDropdown');
		if (dropdown) {
			dropdown.classList.toggle('no-display');
		}
	}

	toggleLimits(): void {
		this.showLimits = !this.showLimits;
		this.$localStorage.showLimits = this.showLimits;
	}

	showFilter(): boolean {
		return this.checkLocation() && this.filterActivated && !this.manageTemplatesSelected;
	}

	doShowLimits(position: any, measurement: string): boolean {
		if (measurement === 'PressurePa') {
			return this.showLimits || position.LocationOverviewPosition[measurement] != null;
		}
		return this.showLimits;
	}

	checkLocation(): boolean {
		const path = window.location.href.substring(window.location.href.lastIndexOf('/') + 1);
		return path !== 'LocationOverview' && path !== '';
	}

	isTemplateSynched(template: any, tempPropTarget: string): boolean {
		if (template.UnitConditionTempActual != null) {
			return template.UnitConditionTemp[tempPropTarget] === template.UnitConditionTempActual[tempPropTarget];
		}
		return false;
	}

	showDashForActualValue(template: any, tempPropTarget: string): boolean {
		return (template.UnitConditionTempActual === null || template.UnitConditionTempActual[tempPropTarget] === null);
	}

	templateNotSynched(position: any): boolean {
		return (
			position.LocationOverviewPosition.isTemplateSynched != null &&
			position.LocationOverviewPosition.isTemplateSynched === false
		);
	}

	showTemplateDetail(position: any): void {
		document.getElementById('templateName')?.classList.add('notClickable');

		const templateId = position.LocationOverviewPosition.UnitConditionTemplateId;
		this.isUnitAttached = position.Decoration.UnitsOnlineCount > 0;

		if (templateId !== null) {
			this.addTemplateIdToPreselectedList(templateId);
			this.positionIdToChangeTemplate = position.LocationOverviewPosition.PositionId;
			this.templates = [];

			this.http.get<any>(`/api/UnitConditionTemplate/GetTemplateAndUnitValuesById?positionId=${position.Id}`)
				.subscribe((response) => {
					if (response !== '' && response !== null && response !== 'undefined') {
						const templateSelected = response;

						this.selectedUnitType = position.LocationOverviewPosition.UnitTypeName !== null
							? position.LocationOverviewPosition.UnitTypeName
							: templateSelected.UnitType;

						if (position.LocationOverviewPosition.UnitId === null) {
							this.isUnitTypeSelectedFromTemplateDetail = true;
						}

						this.templates!.push(templateSelected);
						this.templateClicked = true;
						this.selectedPosition = position;

						const isSynched = position.LocationOverviewPosition.isTemplateSynched;
						if (isSynched === false) {
							this.isSynched = false;
						}
					} else {
						this.templates = [];
					}
				});
		}
	}

	templateAssignedOrRemovedFeedback(positionsToApply: any[], removeTemplate: boolean): void {
		let listOfPositions = '';
		let applyToPosition = '';
		let message = '';

		for (let i = 0; i < positionsToApply.length; i++) {
			if (positionsToApply.length > 1) {
				if (i < positionsToApply.length - 2) {
					listOfPositions += ` ${positionsToApply[i].PositionName},`;
				} else if (i === positionsToApply.length - 2) {
					listOfPositions += ` ${positionsToApply[i].PositionName}`;
				} else {
					listOfPositions += ` ${this.Content.getContent('LocationOverview.and')} ${positionsToApply[i].PositionName}`;
				}
			} else {
				listOfPositions = ` ${positionsToApply[i].PositionName}`;
			}
		}

		if (!removeTemplate) {
			applyToPosition = positionsToApply.length > 1
				? this.Content.getContent('LocationOverview.wasAssignedToPositions')
				: this.Content.getContent('LocationOverview.wasAssignedToPosition');
			message = `${this.Content.getContent('LocationOverview.Template')} ${positionsToApply[0].UnitConditionTemplate.Name}${applyToPosition}${listOfPositions}`;
		} else {
			applyToPosition = positionsToApply.length > 1
				? this.Content.getContent('LocationOverview.TemplateRemovedFromPositions')
				: this.Content.getContent('LocationOverview.TemplateRemovedFromPosition');
			message = `${applyToPosition}${listOfPositions}`;
		}

		this.snack.open(message, 'Close', {
			duration: 30000, // in ms
			horizontalPosition: 'center',
			verticalPosition: 'bottom'
		});
	}

	applyAndSyncTemplate(doApply: boolean): void {
		this.applyOrRemoveTemplate(doApply);
		// this.applyOrRemoveTemplate(false);
	}

	valueNotSynched(rule: any): string {
		if (!this.isSynched && rule !== null) {
			return 'unitValuesRed';
		} else {
			return '';
		}
	}

	getHeaderTitle(header: string): string {
		return this.Content.getContent(`ManageUnitTemplates.${header}`);
	}

	notExhaustAirFlowAndUpsBattery(header: string): boolean {
		return header !== 'ExhaustAirFlow' && header !== 'UpsBattery';
	}

	notPressureDiffAndUsedOn(header: string): boolean {
		return header !== 'PressureDiff' && header !== 'UsedOn';
	}

	getNodeByPath(rootNodes: any[], pathString: string = '', previousNode: boolean): any {
		let node: any = null;
		let path: string[];

		if (previousNode) {
			const tempPath = pathString.split('/');
			path = [tempPath[0]];
		} else {
			path = pathString.split('/');
		}

		for (const segment of path) {
			if (segment) {
				node = rootNodes.find((n: any) => n.Name === segment);
				if (node && node.Children) {
					rootNodes = node.Children;
				} else {
					break;
				}
			}
		}

		return node;
	}

	getFinalLocationOverview() {
		this.http.get<boolean>('/api/Tenant/HasSiteLevel').subscribe((hasSite) => {
			this.siteLevelActive = hasSite;
			this.getLocationOverview(this.siteLevelActive);
		});
	}

	getCurrentUser() {
		this.http.get<any>('/api/user/CurrentUser').subscribe((result) => {
			this.currentUser = result;
			this.readMode =
				this.currentUser.GlobalAppRoleId.localeCompare('00000000-0000-0000-0000-000000000002') === 1;
		});
	}
}
